import React from 'react';
import { ChevronDownIcon } from '@radix-ui/react-icons';
import MessageContentRenderer from './MessageContentRenderer';
import AnimatedEllipsisText from '../common/AnimatedEllipsisText';

interface ReasoningDropdownProps {
  reasoning: string;
  initiallyOpen?: boolean;
  dark?: boolean;
  title?: string;
  autoCollapse?: boolean; // When true, dropdown will auto-collapse
  isStreaming?: boolean; // Whether content is still streaming (keep open until done)
  placeholderOptions?: string[]; // For cycling placeholders when reasoning is empty
}

export const ReasoningDropdown: React.FC<ReasoningDropdownProps> = ({ 
  reasoning, 
  initiallyOpen = false, 
  dark = false, 
  title = 'Reasoning',
  autoCollapse = false,
  isStreaming = false,
  placeholderOptions
}) => {
  const [open, setOpen] = React.useState(initiallyOpen);
  const hasAutoCollapsedRef = React.useRef(false);
  
  // Auto-collapse when thinking is complete
  React.useEffect(() => {
    if (autoCollapse && !isStreaming && !hasAutoCollapsedRef.current) {
      hasAutoCollapsedRef.current = true;
      setOpen(false);
    }
  }, [autoCollapse, isStreaming]);
  
  // Reset auto-collapse flag when reasoning content changes (new request)
  React.useEffect(() => {
    if (isStreaming) {
      hasAutoCollapsedRef.current = false;
      // Keep dropdown open while streaming
      if (autoCollapse) {
        setOpen(true);
      }
    }
  }, [isStreaming, autoCollapse]);
  

  // Always render dropdown UI. If no reasoning and placeholderOptions provided, show cycling placeholder in content area.
  const showCyclingPlaceholder = (!reasoning || !reasoning.trim()) && placeholderOptions && placeholderOptions.length > 0;
  if (!reasoning && !showCyclingPlaceholder) return null;

  // Detect if this is a simple placeholder (no markdown, just a short phrase)
  const isSimplePlaceholder =
    reasoning.trim().length < 60 && !reasoning.includes('**') && !reasoning.includes('\n') && !reasoning.includes('<') && !reasoning.includes('>');

  // Formatting: ensure bold section titles start on their own paragraph.
  const formatReasoning = (raw: string) => {
    let out = raw.replace(/<\/thinking>/gi, '</thinking>\n\n');
    out = out.replace(/([^\n])((\*\*[A-Z][^*]+\*\*))/g, (m, prev, bold) => `${prev}\n\n${bold}`);
    out = out.replace(/(\*\*[A-Z][^*]+\*\*)(?!\n\n)(?=\S)/g, (m) => `${m}\n\n`);
    return out;
  };
  const formatted = formatReasoning(reasoning.trim());

  return (
    <div className="mb-4" style={{ border: dark ? '1px solid var(--color-accent)' : '1px solid var(--color-border)', borderRadius: '8px', overflow: 'hidden' }}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        aria-expanded={open}
        aria-label={open ? `Hide ${title.toLowerCase()} trace` : `Show ${title.toLowerCase()} trace`}
        style={{
          width: '100%',
          textAlign: 'left',
          background: 'transparent',
          border: 'none',
          padding: '10px 14px',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          fontWeight: 600,
          color: 'var(--color-text)'
        }}
      >
        <ChevronDownIcon
          style={{
            transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform .25s ease',
            width: 18,
            height: 18,
            flexShrink: 0,
          }}
        />
        <span style={{ paddingTop: '2px'}}>{title}</span>
        <span style={{ marginLeft: 'auto', fontSize: '11px', opacity: 0.6 }}>{open ? 'click to collapse' : 'click to expand'}</span>
      </button>
      {open && (
        <div style={{ padding: '0 14px 12px', fontSize: '0.75rem', lineHeight: '1.5' }}>
          {showCyclingPlaceholder ? (
            <AnimatedEllipsisText options={placeholderOptions} className="text-[var(--color-text)] text-xs" />
          ) : isSimplePlaceholder ? (
            <AnimatedEllipsisText text={reasoning.trim()} className="text-[var(--color-text)] text-xs" />
          ) : (
            <MessageContentRenderer content={formatted} className="text-xs" disableNormalization />
          )}
        </div>
      )}
    </div>
  );
};

export default ReasoningDropdown;